import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '../../../button/src';
import { useLocalizeMessage } from '../../../i18n/src';
import { ToastNotification } from './notification_toast.component';
export const ToastNotificationContext = createContext({
    toasts: [],
    addNotification: () => {
        throw new Error('Toast Notification Context is not defined.');
    },
    removeNotification: () => {
        throw new Error('Toast Notification Context is not defined.');
    },
    clearAllNotifications: () => {
        throw new Error('Toast Notification Context is not defined.');
    },
});
const POSITION_STYLES = {
    'bottom-end': 'nucleus-bottom-0 nucleus-right-0',
    'bottom-start': 'nucleus-bottom-0 nucleus-left-0',
    'top-end': 'nucleus-right-0 nucleus-top-0',
    'top-start': 'nucleus-left-0 nucleus-top-0',
};
const DEFAULT_TIMEOUT = 10000;
const TOAST_GAP = 12;
export const ToastNotificationProvider = ({ children, position = 'bottom-start', showClearAllButton, }) => {
    const [toasts, setToasts] = useState([]);
    const [isHovered, setIsHovered] = useState(false);
    const [isTouchExpanded, setIsTouchExpanded] = useState(false);
    const toastRefs = useRef([]);
    const viewportRef = useRef(null);
    const translate = useLocalizeMessage();
    const stackIsOpen = isHovered || isTouchExpanded;
    const removeNotification = (id) => {
        setToasts((prevToasts) => {
            const newToasts = prevToasts.filter((toast) => toast.id !== id);
            if (newToasts.length === 0) {
                setIsHovered(false);
                setIsTouchExpanded(false);
            }
            return newToasts;
        });
        toastRefs.current = toastRefs.current.filter((ref) => (ref === null || ref === void 0 ? void 0 : ref.id) !== id);
    };
    const addNotification = (toast) => {
        setToasts((prevToasts) => {
            var _a, _b;
            const id = (_a = toast.id) !== null && _a !== void 0 ? _a : uuidv4();
            const timeout = (_b = toast.timeout) !== null && _b !== void 0 ? _b : DEFAULT_TIMEOUT;
            return [...prevToasts, Object.assign(Object.assign({}, toast), { id, timeout })];
        });
    };
    const clearAllNotifications = () => {
        setToasts([]);
        toastRefs.current = [];
        setIsHovered(false);
        setIsTouchExpanded(false);
    };
    useEffect(() => {
        if (!isTouchExpanded) {
            return;
        }
        const collapseOnOutsideTap = (event) => {
            const viewport = viewportRef.current;
            if (viewport && event.target instanceof Node && viewport.contains(event.target)) {
                return;
            }
            setIsTouchExpanded(false);
        };
        document.addEventListener('pointerdown', collapseOnOutsideTap);
        return () => document.removeEventListener('pointerdown', collapseOnOutsideTap);
    }, [isTouchExpanded]);
    const isTopPositioned = position.includes('top');
    const flexDirection = isTopPositioned ? 'nucleus-flex-col' : 'nucleus-flex-col-reverse';
    // A touch tap emulates mouseenter and never emits mouseleave, so hover would pause auto-dismiss until another tap.
    const pauseWhileHovered = (event) => {
        if (event.pointerType === 'touch') {
            return;
        }
        setIsHovered(true);
    };
    const resumeAfterHover = (event) => {
        if (event.pointerType === 'touch') {
            return;
        }
        setIsHovered(false);
    };
    const toggleTouchStack = (event) => {
        if (event.pointerType !== 'touch') {
            return;
        }
        const { target } = event;
        if (target instanceof Element && target.closest('button, a')) {
            return;
        }
        setIsTouchExpanded((open) => !open);
    };
    let totalToastHeight = 0;
    if (stackIsOpen) {
        toastRefs.current.forEach((ref, index) => {
            var _a;
            totalToastHeight += ((_a = ref === null || ref === void 0 ? void 0 : ref.scrollHeight) !== null && _a !== void 0 ? _a : 0) + (index < toastRefs.current.length - 1 ? TOAST_GAP : 0);
        });
    }
    const getToastTranslateAmount = (index) => {
        // caculate the translate amount for each toast.
        // When not being hovered, each toast is staggered by 8px related to the previous toast.
        // When being hovered, each toast is staggered by the previous toast's height, plus a 12px gap.
        let translateAmount = (isTopPositioned ? 8 : -8) * Math.min(index, 3);
        if (stackIsOpen && index > 0) {
            translateAmount = 0;
            for (let i = index - 1; i >= 0; i--) {
                const ref = toastRefs.current[i];
                if (ref) {
                    if (isTopPositioned) {
                        translateAmount += ref.scrollHeight + TOAST_GAP;
                    }
                    else {
                        translateAmount -= ref.scrollHeight + TOAST_GAP;
                    }
                }
            }
        }
        return translateAmount;
    };
    const shouldScroll = isTopPositioned && stackIsOpen && totalToastHeight > window.innerHeight;
    return (_jsxs(ToastNotificationContext.Provider, { value: { addNotification, removeNotification, clearAllNotifications, toasts }, children: [children, toasts.length > 0 &&
                createPortal(_jsxs("div", { ref: viewportRef, "data-testid": 'nucleus-toast-viewport', onPointerEnter: pauseWhileHovered, onPointerLeave: resumeAfterHover, onPointerUp: toggleTouchStack, className: `${stackIsOpen ? 'nucleus-pointer-events-auto' : 'nucleus-pointer-events-none'} nucleus-fixed nucleus-box-border nucleus-z-[100] nucleus-w-full nucleus-max-w-full nucleus-p-4 sm:nucleus-w-modal-base sm:nucleus-max-w-full ${POSITION_STYLES[position]} ${shouldScroll ? 'nucleus-max-h-dvh nucleus-overflow-y-auto nucleus-overflow-x-hidden' : ''}`, children: [_jsx("div", { className: `nucleus-w-full nucleus-max-h-dvh nucleus-relative nucleus-flex nucleus-flex-col nucleus-gap-4 nucleus-group ${flexDirection}`, style: {
                                height: totalToastHeight,
                            }, children: toasts.map((toast, index) => {
                                const translateAmount = getToastTranslateAmount(index);
                                return (_jsx("div", { id: toast.id, ref: (el) => (toastRefs.current[index] = el), className: `nucleus-pointer-events-auto nucleus-box-border nucleus-absolute nucleus-transition-transform nucleus-duration-300 nucleus-ease-out nucleus-w-full ${POSITION_STYLES[position]} ${!stackIsOpen && toasts.length > 1 && index !== toasts.length - 1 ? 'nucleus-max-h-10 nucleus-overflow-y-hidden' : ''} ${!stackIsOpen && toasts.length > 1 && index !== toasts.length - 1 ? 'nucleus-filter nucleus-brightness-95' : ''}`, style: {
                                        transform: `scaleX(${stackIsOpen ? 1 : 1 - 0.025 * (Math.min(toasts.length, 3) - 1 - Math.min(index, 3))}) translateY(${translateAmount}px)`,
                                    }, children: _jsx(ToastNotification, Object.assign({}, toast, { timeout: isHovered ? 0 : toast.timeout, onClose: () => removeNotification(toast.id), position: position })) }, toast.id));
                            }) }), showClearAllButton && stackIsOpen && toasts.length > 1 && (_jsx("div", { className: `nucleus-pointer-events-auto nucleus-box-border nucleus-absolute nucleus-w-full nucleus-transition-transform nucleus-duration-300 nucleus-ease-out nucleus-flex ${isTopPositioned ? 'nucleus-top-0' : 'nucleus-bottom-0'}`, style: {
                                transform: `translateY(${(totalToastHeight + 2 * TOAST_GAP) * (isTopPositioned ? 1 : -1)}px)`,
                            }, children: _jsx(Button, { size: 'xs', label: translate('Clear all'), variant: 'transparent', onClick: clearAllNotifications }) }))] }), document.body)] }));
};
//# sourceMappingURL=notification_toast.provider.js.map